// src/pages/AdminLogin.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Shield,
  Lock,
  Mail,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import Logo from '../assets/images/visit_logo.png';

const AdminLogin = () => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = login, 2 = 2FA
  const [otp, setOtp] = useState('');
  const [tempUserId, setTempUserId] = useState(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false,
    termsAccepted: true, // ✅ default ticked
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // helper: save auth info and redirect by role
  const handleLoginSuccess = (token, role, user) => {
    // Prepare user data for header/profile
    const userData = user
      ? {
          id: user.id,
          name: user.name,
          email: user.email,
        }
      : null;

    if (formData.remember) {
      localStorage.setItem('authToken', token);
      localStorage.setItem('authRole', role);
      if (userData) {
        localStorage.setItem('authUser', JSON.stringify(userData));
      }
    } else {
      sessionStorage.setItem('authToken', token);
      sessionStorage.setItem('authRole', role);
      if (userData) {
        sessionStorage.setItem('authUser', JSON.stringify(userData));
      }
    }

    // Decide redirect path
    if (role === 'admin') {
      navigate('/dashboard', { replace: true });
    } else if (role === 'client') {
      navigate('/clients/dashboard', { replace: true });
    } else {
      // Fallback: just send to main dashboard
      navigate('/dashboard', { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // ✅ Block if terms not accepted
    if (!formData.termsAccepted) {
      setError('You must agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setIsLoading(true);

    try {
      // Step 1: email + password (admin or client)
      const res = await fetch(
        'https://patrol-visit.patroll-management.nissiindia.com/api/login',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // If backend says 2FA is needed (admin with 2FA)
      if (data.require2FA) {
        setTempUserId(data.tempUserId);
        setStep(2);
      } else {
        // No 2FA (client OR admin without 2FA)
        if (!data.token || !data.role) {
          throw new Error('Invalid server response: missing token or role');
        }
        // Pass user object if available (for header)
        handleLoginSuccess(data.token, data.role, data.user);
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const res = await fetch(
        'https://patrol-visit.patroll-management.nissiindia.com/api/verify-2fa',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tempUserId,
            code: otp,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || '2FA failed');
      }

      if (!data.token || !data.role) {
        throw new Error('Invalid server response: missing token or role');
      }

      // Admin after 2FA – also store user
      handleLoginSuccess(data.token, data.role, data.user);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f12_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f12_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Main container */}
      <div className="w-full max-w-md relative z-10">
        {/* Card */}
        <div className="bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50 overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-10 pb-8 border-b border-slate-700/50">
            <div className="flex items-center justify-center mb-6">
              <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-xl bg-slate-900/60 flex items-center justify-center">
                <img
                  src={Logo}
                  alt="Patrol Visit Logo"
                  className="w-16 h-16 object-contain"
                />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-white text-center mb-2">
              Admin Control Panel
            </h1>
            <p className="text-slate-400 text-sm text-center">
              Patrol Visit by EBlackDog.com
            </p>
          </div>

          {/* System Status */}
          <div className="px-8 pt-6">
            <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg mb-6">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <div className="absolute inset-0 animate-ping opacity-75">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  </div>
                </div>
                <span className="text-sm text-emerald-300 font-medium">
                  System Online
                </span>
              </div>
              <span className="text-xs text-emerald-400">
                All services operational
              </span>
            </div>

            {/* Admin Badge */}
            <div className="flex items-center justify-center gap-2 mb-6 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <Lock className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-300 font-semibold">
                Administrator / Client Access
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="px-8 pb-8">
            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/40 rounded-lg text-xs text-red-300 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            {/* Step 1: Login form */}
            {step === 1 && (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-slate-300 mb-2"
                  >
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500">
                      <Mail className="w-5 h-5" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="user@example.com"
                      autoComplete="email"
                      className="w-full pl-11 pr-4 py-3 bg-slate-900/50 border-2 border-slate-700 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 outline-none transition-all duration-300 text-white placeholder-slate-500"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-slate-300 mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500">
                      <Lock className="w-5 h-5" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      className="w-full pl-11 pr-12 py-3 bg-slate-900/50 border-2 border-slate-700 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 outline-none transition-all duration-300 text-white placeholder-slate-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-purple-400 transition-colors duration-200"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="remember"
                      checked={formData.remember}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-2 focus:ring-purple-500/50 cursor-pointer"
                    />
                    <span className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors duration-200">
                      Keep me signed in
                    </span>
                  </label>
                  
                </div>

                {/* ✅ Terms checkbox */}
                <div className="flex items-start gap-2 p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleInputChange}
                    className="mt-1 w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-2 focus:ring-purple-500/50 cursor-pointer"
                  />
                  <p className="text-xs text-slate-300 leading-relaxed">
                    I agree to the{' '}
                    <a
                      href="https://www.eblackdog.com/terms-0001.html"
                      target="_blank"
                      rel="noreferrer"
                      className="text-purple-400 hover:text-purple-300 underline"
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.eblackdog.com/privacy.html"
                      target="_blank"
                      rel="noreferrer"
                      className="text-purple-400 hover:text-purple-300 underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>

                {/* Security notice */}
                <div className="flex items-start gap-2 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-300/90">
                    Admin and client accounts are monitored. All login attempts
                    are logged for security purposes.
                  </p>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 ${
                    isLoading
                      ? 'bg-slate-700 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 hover:shadow-purple-500/50 hover:shadow-xl hover:scale-[1.02] active:scale-100'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Authenticating...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Shield className="w-5 h-5" />
                      <span>Access Control Panel</span>
                    </div>
                  )}
                </button>
              </form>
            )}

            {/* Step 2: 2FA form */}
            {step === 2 && (
              <form onSubmit={handleVerifyOtp} className="space-y-5">
                <div className="text-center mb-2">
                  <p className="text-sm text-slate-300 font-medium">
                    Two-Factor Authentication
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Enter the 6-digit verification code sent to your registered
                    contact.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, ''))
                    }
                    placeholder="••••••"
                    className="w-full text-center tracking-[0.5em] text-xl py-3 bg-slate-900/50 border-2 border-slate-700 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 outline-none transition-all duration-300 text-white placeholder-slate-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className={`w-full py-3 px-6 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 ${
                    isLoading || otp.length !== 6
                      ? 'bg-slate-700 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 hover:shadow-purple-500/50 hover:shadow-xl hover:scale-[1.02] active:scale-100'
                  }`}
                >
                  {isLoading ? 'Verifying...' : 'Verify Code'}
                </button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-xs text-slate-400 hover:text-slate-200 mt-1"
                >
                  &larr; Back to login
                </button>
              </form>
            )}

            {/* Support link */}
            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500">
                Need help?{' '}
                <a
                  href="https://nissiinfotech.typeform.com/to/JEXvUNj7"
                  target="_blank"
                  rel="noreferrer"
                  className="text-purple-400 font-medium hover:text-purple-300 hover:underline"
                >
                  Contact System Administrator
                </a>
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-5 bg-slate-900/50 border-t border-slate-700/50">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Patrol Visit Admin v1.0</span>
              
              
            </div>
          </div>
        </div>

        {/* Security badges */}
        <div className="mt-6 flex items-center justify-center gap-4 text-xs text-slate-400">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            <span>SSL Secured</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full" />
            <span>2FA Enabled (Admin)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-purple-500 rounded-full" />
            <span>Encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
